import api from './api';
import { flagEmoji } from '@tulasihome/shared';

export type ShippingProviderCode = 'UKRPOSHTA' | 'NOVAPOST';

export type ShippingOptionCode = 'STANDARD' | 'EXPRESS';
export type ShippingOptionStatus = 'OK' | 'FALLBACK';
export type DeliveryMode = 'BRANCH' | 'LOCKER' | 'COURIER' | 'PICKUP_POINT';

export interface ShippingOfficeSuggestion {
  readonly id: string;
  readonly label: string;
  readonly address?: string;
  readonly type?: string;
  readonly payload?: any;
}

export interface ShippingCountryOption {
  readonly code: string;
  readonly label: string;
  readonly emoji?: string;
}

export interface PriceAmount {
  readonly amount: number;
  readonly currency: string;
}

export interface EtaWindow {
  readonly minDays?: number;
  readonly maxDays?: number;
}

export interface ShippingQuoteOption {
  readonly provider?: ShippingProviderCode;
  readonly option: ShippingOptionCode;
  readonly status: ShippingOptionStatus;
  readonly price: PriceAmount;
  readonly packageType?: string;
  readonly eta?: EtaWindow;
  readonly breakdown?: string;
  readonly warnings?: ReadonlyArray<string>;
  readonly fingerprint?: string;
}

export interface QuoteMetadata {
  readonly hit: boolean;
  readonly ttlSeconds: number;
}

export interface ShippingQuoteResponse {
  readonly provider: ShippingProviderCode;
  readonly options: ReadonlyArray<ShippingQuoteOption>;
  readonly cache?: QuoteMetadata;
}

export interface ShippingQuoteDestination {
  readonly countryCode: string;
  readonly region?: string;
  readonly city: string;
  readonly cityId?: string;
  readonly postcode?: string;
  readonly street?: string;
  readonly house?: string;
  readonly apartment?: string;
}

export interface ShippingAddressSuggestion {
  readonly id: string;
  readonly label: string;
  readonly fullName?: string;
  readonly type?: string;
}

export interface ShippingQuotePackageDimensions {
  readonly weightGrams: number;
  readonly lengthIn: number;
  readonly widthIn: number;
  readonly heightIn: number;
}

export interface ShippingQuoteDelivery {
  readonly mode: DeliveryMode;
  readonly officeId?: string;
}

export interface ShippingQuoteRequestOptions {
  readonly excludePackageTypes?: ReadonlyArray<string>;
}

export interface ShippingQuoteRequestPayload {
  readonly orderId: string;
  readonly destination: ShippingQuoteDestination;
  readonly package: ShippingQuotePackageDimensions;
  readonly delivery: ShippingQuoteDelivery;
  readonly quoteOptions?: ShippingQuoteRequestOptions;
}

type ShippingCountryApiPayload = {
  readonly id: string;
  readonly englishName: string;
};

const mapCountryPayload = (country: ShippingCountryApiPayload): ShippingCountryOption => ({
  code: country.id,
  label: country.englishName,
  emoji: flagEmoji(country.id),
});

export const getShippingCountries = async (
  provider: ShippingProviderCode,
): Promise<ShippingCountryOption[]> => {
  try {
    const { data } = await api.get<{ data: ShippingCountryApiPayload[] }>(`/shipping/${provider}/countries`);
    return Array.isArray(data?.data) ? data.data.map(mapCountryPayload) : [];
  } catch {
    return [];
  }
};

export const getShippingQuote = async (
  provider: ShippingProviderCode,
  payload: ShippingQuoteRequestPayload,
): Promise<ShippingQuoteResponse> => {
  const { data } = await api.post<{ data: ShippingQuoteResponse }>(`/shipping/${provider}/quote`, payload);
  return data.data;
};

export const getShippingCities = async (
  provider: ShippingProviderCode,
  params: {
    readonly countryCode?: string;
    readonly regionId?: string;
    readonly districtId?: string;
    readonly query: string;
    readonly signal?: AbortSignal;
  },
): Promise<ShippingAddressSuggestion[]> => {
  const { data } = await api.get<{ data: ShippingAddressSuggestion[] }>(`/shipping/${provider}/ua/cities`, {
    params: {
      countryCode: params.countryCode,
      regionId: params.regionId,
      districtId: params.districtId,
      query: params.query,
    },
    signal: params.signal,
  });
  return Array.isArray(data?.data) ? data.data : [];
};

export const getShippingOffices = async (
  provider: ShippingProviderCode,
  params: {
    readonly countryCode: string;
    readonly cityId: string;
    readonly query?: string;
  },
): Promise<ShippingOfficeSuggestion[]> => {
  const { data } = await api.get<{ data: ShippingOfficeSuggestion[] }>(`/shipping/${provider}/ua/offices`, {
    params,
  });
  return Array.isArray(data?.data) ? data.data : [];
};
